import { useEffect, useState } from 'react';
import type { Canal, Incidencia } from '../../api/ops/canales';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { Campo } from '../../componentes/Campo';
import { Chip, type EstadoChip } from '../../componentes/Chip';
import { Deshacer } from '../../componentes/Deshacer';
import { Desplegable } from '../../componentes/Desplegable';
import { Icono } from '../../componentes/Icono';
import { Interruptor } from '../../componentes/Interruptor';
import { PanelLateral } from '../../componentes/PanelLateral';
import { Segmentos } from '../../componentes/Segmentos';
import { Ventana } from '../../componentes/Ventana';
import { useEnvioRetrasado } from '../../logica/envioRetrasado';
import { avisoBreveDeError } from '../../logica/estados';
import { diaMes, diaYHora } from '../../logica/fechasCortas';
import { navegador } from '../../logica/navegador';
import { haceCuanto } from '../../logica/tiempo';
import { usePermisos } from '../../permisos/permisos';
import type { PropsApartado } from './Canales';
import { RUTA_CANALES_DE_ENVIO, RUTA_PLAN } from './ListaCanales';
import { AYUDA_LIMITE_META, CALIDAD, ESTADO_CANAL, impactoDesconectar, INCIDENCIA, LIMITE_META, nombreDe, PAGO, permitido, PROVEEDOR, remitenteDe, RESPUESTAS_EMAIL, tieneAltaManual, TIPO, vivo } from './reglas';

/**
 * Ficha de un canal (11.1, `getChannel`): Salud · Incidencias · Ajustes. «Comprobar ahora» (`runChannelHealthCheck`,
 * también el Encargado), «Reconectar» si la incidencia abierta lo pide (11.2 con `replaces_channel_id`), Ajustes con
 * `updateChannel` (`If-Match`, «Deshacer» 5 s) y desconectar con motivo y ventana de impacto (`disconnectChannel`, también
 * en solo lectura). El enlace «Desactivar el servicio en Meta» sale cuando la API da `provider_links.deactivate_url`
 * (WhatsApp, con proveedor YCloud, e Instagram, G687) y solo a quien gestiona los canales. El email dice desde dónde sale
 * (`email_sender`, G721) y en Ajustes se cambia el nombre visible (`email_from_name`). El chat de la web tiene su ficha
 * mínima (`FichaChatWeb`, G715).
 */
const MIN_MOTIVO = 10;

interface Dato {
    k: string;
    v: string;
    chip?: EstadoChip;
    sub?: string;
}

export function FichaCanal(props: PropsApartado & { canal: Canal; abierta: Incidencia | null; onCerrar: () => void; onReconectar: (c: Canal) => void; onDesconectado: (texto: string) => void }) {
    const { ops, negocio, movil, soloLectura, navegar, datos, recargarDatos, canal, abierta, onCerrar, onReconectar, onDesconectado } = props;
    const { puede } = usePermisos();
    const gestiona = puede('canales.canales.gestionar');
    const avisar = useAvisoBreve();
    const retraso = useEnvioRetrasado();
    const [pestana, setPestana] = useState(0);
    const [actual, setActual] = useState<{ canal: Canal; version: string } | null>(null);
    const [incidencias, setIncidencias] = useState<Incidencia[] | null>(null);
    const [comprobando, setComprobando] = useState(false);
    const [comprobado, setComprobado] = useState<string | null>(null);
    const [ajustes, setAjustes] = useState({ nombre: nombreDe(canal), sede: canal.location_id ?? '', porDefecto: canal.is_default, remitente: canal.email_sender?.from_name ?? '' });
    const [desconectar, setDesconectar] = useState(false);
    const [motivo, setMotivo] = useState('');
    const [intentado, setIntentado] = useState(false);
    const [ocupado, setOcupado] = useState(false);

    useEffect(() => {
        let vivoEfecto = true;
        ops.getChannel(negocio, canal.id)
            .then((r) => {
                if (vivoEfecto) {
                    setActual({ canal: r.datos, version: r.version ?? '' });
                }
            })
            .catch(() => undefined);
        ops.listChannelIncidents(negocio, canal.id, { limit: 50 })
            .then((r) => {
                if (vivoEfecto) {
                    setIncidencias(r.data);
                }
            })
            .catch(() => {
                if (vivoEfecto) {
                    setIncidencias([]);
                }
            });
        return () => {
            vivoEfecto = false;
        };
    }, [ops, negocio, canal.id]);

    const c = actual?.canal ?? canal;
    const apagado = !vivo(c);
    const tipo = TIPO[c.channel_type] ?? { nombre: c.channel_type, icono: 'messages-square' as const };
    const soloListas = c.channel_type === 'instagram' && permitido('instagram', datos.bloques).soloListas === true;
    const pestanas = [
        { etiqueta: 'Salud', icono: 'gauge' as const },
        { etiqueta: 'Incidencias', icono: 'triangle-alert' as const },
        ...(gestiona && !apagado ? [{ etiqueta: 'Ajustes', icono: 'sliders-horizontal' as const }] : []),
    ];
    const indice = Math.min(pestana, pestanas.length - 1);
    // El chat de la web no se reconecta a mano (G685: sin alta manual; `startChannelConnection` con él da `422`).
    const reconectable = gestiona && !soloLectura && abierta !== null && INCIDENCIA[abierta.kind][3] && tieneAltaManual(c.channel_type);
    const remitenteActual = c.email_sender?.from_name ?? '';
    const cambiaRemitente = c.channel_type === 'email' && c.email_sender != null && ajustes.remitente.trim() !== '' && ajustes.remitente.trim() !== remitenteActual;
    const sinCambios = ajustes.nombre.trim() === nombreDe(c) && ajustes.sede === (c.location_id ?? '') && ajustes.porDefecto === c.is_default && !cambiaRemitente;
    const sede = (id: string | null | undefined) => (id == null ? 'Todos los locales' : (datos.sedes.find((s) => s.id === id)?.nombre ?? ''));
    const [estado, tono] = ESTADO_CANAL[c.status];
    const meta = c.channel_type === 'whatsapp' || c.channel_type === 'instagram';

    const comprobar = () => {
        setComprobando(true);
        setComprobado(null);
        ops.runChannelHealthCheck(negocio, c.id)
            .then((r) => {
                setComprobado(`Comprobado ahora. ${(r.open_incidents ?? []).length > 0 ? 'La incidencia sigue abierta.' : 'Todo funciona.'}`);
                recargarDatos();
            })
            .catch((e: unknown) => { avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido comprobar.' }); })
            .finally(() => { setComprobando(false); });
    };

    const guardar = () => {
        if (actual === null) {
            return;
        }
        const cambio: { display_name?: string; location_id?: string | null; is_default?: boolean; email_from_name?: string } = {};
        if (ajustes.nombre.trim() !== nombreDe(c) && ajustes.nombre.trim() !== '') {
            cambio.display_name = ajustes.nombre.trim();
        }
        if (ajustes.sede !== (c.location_id ?? '')) {
            cambio.location_id = ajustes.sede === '' ? null : ajustes.sede;
        }
        if (ajustes.porDefecto !== c.is_default) {
            cambio.is_default = ajustes.porDefecto;
        }
        if (cambiaRemitente) {
            cambio.email_from_name = ajustes.remitente.trim();
        }
        if (Object.keys(cambio).length === 0) {
            return;
        }
        const version = actual.version;
        retraso.programar({
            texto: `«${ajustes.nombre.trim() || nombreDe(c)}» guardado.`,
            donde: 'ajustes',
            enviar: () => {
                ops.updateChannel(negocio, c.id, cambio, version)
                    .then((r) => {
                        setActual({ canal: r.datos, version: r.version ?? '' });
                        recargarDatos();
                    })
                    .catch((e: unknown) => {
                        avisar({ tipo: 'error', texto: e instanceof ErrorApi && e.codigo === 'version_conflict' ? 'Otra persona ha cambiado el canal. Ciérralo y ábrelo otra vez.' : (avisoBreveDeError(e) ?? 'No se ha podido guardar.') });
                    });
            },
            alDeshacer: () => { setAjustes({ nombre: nombreDe(c), sede: c.location_id ?? '', porDefecto: c.is_default, remitente: remitenteActual }); },
        });
    };

    const hacerDesconexion = () => {
        if (motivo.trim().length < MIN_MOTIVO) {
            setIntentado(true);
            return;
        }
        setOcupado(true);
        ops.disconnectChannel(negocio, c.id, motivo.trim())
            .then((r) => {
                setDesconectar(false);
                onDesconectado(`«${nombreDe(c)}» desconectado. Sus conversaciones se conservan.${r.provider_links?.deactivate_url != null ? ' Si quieres, desactiva también el servicio en Meta desde su ficha.' : ''}`);
            })
            .catch((e: unknown) => { avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido desconectar.' }); })
            .finally(() => { setOcupado(false); });
    };

    // ── Salud ──
    const hechos: Dato[] = [];
    const conChip = (k: string, par: [string, EstadoChip] | undefined, sub?: string): Dato => (par === undefined ? { k, v: '—' } : par[1] === 'confirmed' ? { k, v: par[0], ...(sub === undefined ? {} : { sub }) } : { k, v: par[0], chip: par[1], ...(sub === undefined ? {} : { sub }) });
    if (meta && !apagado) {
        const q = c.quality_rating ?? 'unknown';
        hechos.push(conChip('Calidad en Meta', CALIDAD[q], q === 'yellow' || q === 'red' ? 'Baja si te bloquean o marcan como spam' : undefined));
        if (c.channel_type === 'whatsapp') {
            hechos.push({ k: 'Límite de Meta', v: c.messaging_limit_tier == null ? '—' : LIMITE_META[c.messaging_limit_tier], sub: AYUDA_LIMITE_META });
        }
        if (c.meta_business_verified != null) {
            hechos.push(conChip('Negocio en Meta', c.meta_business_verified ? ['Verificado', 'confirmed'] : ['Sin verificar', 'pending']));
        }
    }
    if (c.channel_type === 'whatsapp' && !apagado && c.meta_credit_line_status != null) {
        hechos.push(conChip('Pago de los mensajes', PAGO[c.meta_credit_line_status], 'A través de YCloud. Tú no das tarjeta a Meta'));
    }
    if (!apagado && c.channel_type !== 'email') {
        hechos.push({ k: 'Conectado a través de', v: PROVEEDOR[c.provider] ?? c.provider });
    }
    const remitente = c.channel_type === 'email' && !apagado ? remitenteDe(c) : null;
    if (remitente !== null) {
        hechos.push({ k: 'Sale desde', v: remitente.direccion, sub: remitente.sub });
        hechos.push({ k: 'Respuestas', v: 'Al buzón', sub: RESPUESTAS_EMAIL });
    }
    if (c.channel_type === 'instagram' && !apagado) {
        hechos.push({ k: 'Mensajes de inicio', v: c.ice_breakers_configured_at == null ? 'Sin configurar' : `Configurados el ${diaMes(c.ice_breakers_configured_at)}`, sub: 'Necesarios para los enlaces de lista' });
    }
    if (c.channel_type === 'telegram' && !apagado) {
        hechos.push(conChip('Avisos de mensajes nuevos', c.status_reason === 'webhook_failing' ? ['No llegan', 'problem'] : ['Llegan', 'confirmed'], 'El webhook del bot'));
        hechos.push(conChip('Token del bot', c.status_reason === 'token_expired' ? ['Caducado', 'problem'] : ['Guardado', 'confirmed'], 'De @BotFather, cifrado'));
    }
    hechos.push({ k: 'Local', v: sede(c.location_id), ...(c.is_default && !apagado ? { sub: `Por defecto para ${tipo.nombre}` } : {}) });
    if (c.connected_at != null) {
        hechos.push({ k: 'Conectado el', v: diaMes(c.connected_at) });
    }
    if (!apagado) {
        hechos.push({ k: 'Última comprobación', v: c.last_check_at == null ? '—' : haceCuanto(c.last_check_at).replace(/^./, (l) => l.toLocaleUpperCase('es')), sub: 'Cada 5 minutos' });
    }

    const alerta =
        abierta !== null
            ? { clase: abierta.severity === 'down' ? 'roja' : 'amarilla', icono: INCIDENCIA[abierta.kind][0], titulo: INCIDENCIA[abierta.kind][1], texto: INCIDENCIA[abierta.kind][2] + (abierta.severity === 'down' ? ` Mientras tanto, lo que iba por ${tipo.nombre} sale por email.` : '') }
            : apagado
              ? { clase: 'gris', icono: 'unplug' as const, titulo: `Desconectado el ${diaMes(c.disconnected_at)}`, texto: 'Ya no recibe ni envía mensajes. Sus conversaciones y su historial se conservan en el buzón.' }
              : null;

    let accion = '';
    let accionIcono: 'check' | 'refresh-cw' = 'check';
    let onAccion: () => void = () => undefined;
    if (indice === 2) {
        accion = 'Guardar';
        onAccion = guardar;
    } else if (reconectable) {
        accion = 'Reconectar';
        accionIcono = 'refresh-cw';
        onAccion = () => { onReconectar(c); };
    } else if (!apagado) {
        accion = 'Comprobar ahora';
        accionIcono = 'refresh-cw';
        onAccion = comprobar;
    }

    return (
        <>
            <PanelLateral
                abierto
                onCerrar={onCerrar}
                movil={movil}
                tamano="ficha"
                titulo={nombreDe(c)}
                eyebrow={tipo.nombre + (soloListas ? ' · solo listas' : '')}
                subtitulo={c.display_identifier}
                chip={estado}
                chipEstado={tono}
                icono={tipo.icono}
                accion={accion}
                accionIcono={accionIcono}
                ocupado={comprobando}
                ocupadoTexto="Comprobando…"
                inactiva={indice === 2 && (sinCambios || soloLectura || actual === null)}
                onAccion={onAccion}
            >
                <div className="rv-cn-ficha">
                    <Segmentos etiqueta="Partes del canal" movil={movil} items={pestanas} valor={indice} onCambio={(i) => { setPestana(i); setComprobado(null); }} />
                    {indice === 0 && (
                        <>
                            {alerta !== null && (
                                <div className={`rv-cn-alerta rv-cn-alerta--${alerta.clase}`} role="alert">
                                    <Icono nombre={alerta.icono} />
                                    <span>
                                        <strong>{alerta.titulo}</strong>
                                        <span>{alerta.texto}</span>
                                    </span>
                                </div>
                            )}
                            {comprobado !== null && (
                                <div className="rv-cn-comprobado" role="status">
                                    <span className="rv-ca-hecho__baldosa">
                                        <Icono nombre="check" />
                                    </span>
                                    <span>{comprobado}</span>
                                </div>
                            )}
                            <div className="rv-cn-hechos">
                                {hechos.map((h) => (
                                    <div key={h.k} className="rv-cn-hecho">
                                        <span>{h.k}</span>
                                        {h.chip === undefined ? <strong>{h.v}</strong> : <span><Chip estado={h.chip}>{h.v}</Chip></span>}
                                        {h.sub !== undefined && <small>{h.sub}</small>}
                                    </div>
                                ))}
                            </div>
                            {c.channel_type === 'whatsapp' && !apagado && gestiona && (
                                <>
                                    <div className="rv-cn-tarjeta">
                                        <span className="rv-cn-tarjeta__baldosa">
                                            <Icono nombre="package" />
                                        </span>
                                        <span className="rv-cn-tarjeta__texto">Meta no te cobra nada: los mensajes de WhatsApp que Meta factura van incluidos en tu plan y, si te quedas corto, salen de los paquetes extra.</span>
                                        <Boton tipo="secundario" icono="arrow-right" movil={movil} onClick={() => { navegar(RUTA_PLAN); }}>
                                            Ver mis mensajes de WhatsApp
                                        </Boton>
                                    </div>
                                    <div className="rv-cn-tarjeta">
                                        <span className="rv-cn-tarjeta__baldosa">
                                            <Icono nombre="send" />
                                        </span>
                                        <span className="rv-cn-tarjeta__texto">
                                            <strong>Confirmaciones, pases y recordatorios por WhatsApp</strong>
                                            <span>Se deciden en Mensajes con «Enviar por WhatsApp», apagado al empezar. Encendido, cada envío gasta mensajes de tu plan.</span>
                                        </span>
                                        <Boton tipo="secundario" icono="arrow-right" movil={movil} onClick={() => { navegar(RUTA_CANALES_DE_ENVIO); }}>
                                            Ir a Mensajes
                                        </Boton>
                                    </div>
                                </>
                            )}
                            {gestiona && c.provider_links?.deactivate_url != null && (
                                <div className="rv-cn-meta">
                                    <button type="button" className="rv-wb-enlace rv-cn-enlace-rojo" onClick={() => { navegador.abrir(c.provider_links?.deactivate_url ?? ''); }}>
                                        <Icono nombre="external-link" />
                                        <span>Desactivar el servicio en Meta</span>
                                    </button>
                                    <span>{apagado ? 'Ya está desconectado de Reservaria. En Meta sigue tu cuenta con sus plantillas.' : 'Para dejar de usar la cuenta también en Meta. Antes, desconéctalo aquí.'}</span>
                                </div>
                            )}
                        </>
                    )}
                    {indice === 1 && (
                        <>
                            <div className="rv-cn-incidencias">
                                {(incidencias ?? []).map((i) => {
                                    const [icono, titulo, texto] = INCIDENCIA[i.kind];
                                    const resuelta = i.resolved_at != null;
                                    return (
                                        <div key={i.id} className="rv-cn-incidencia">
                                            <span className={`rv-cn-baldosa rv-cn-baldosa--${resuelta ? 'gris' : i.severity === 'down' ? 'roja' : 'amarilla'}`}>
                                                <Icono nombre={icono} />
                                            </span>
                                            <span className="rv-cn-incidencia__texto">
                                                <strong>{titulo}</strong>
                                                <span>{texto}</span>
                                                <small>{`${resuelta ? `Del ${diaYHora(i.opened_at)} al ${diaYHora(i.resolved_at)}` : `Desde ${diaYHora(i.opened_at)}`} · detectada ${String(i.occurrences)} ${i.occurrences === 1 ? 'vez' : 'veces'}`}</small>
                                            </span>
                                            <Chip estado={resuelta ? 'neutral' : i.severity === 'down' ? 'problem' : 'pending'}>{resuelta ? 'Resuelta' : i.severity === 'down' ? 'Caído' : 'Degradado'}</Chip>
                                        </div>
                                    );
                                })}
                                {incidencias !== null && incidencias.length === 0 && <p className="rv-cn-sin">Sin incidencias en este canal.</p>}
                            </div>
                            <p className="rv-ca-nota rv-ca-nota--gris">
                                <Icono nombre="bell" />
                                <span>Lo comprobamos cada 5 minutos. Si un canal se cae, avisamos una vez por incidencia en la campana y al propietario por email.</span>
                            </p>
                        </>
                    )}
                    {indice === 2 && (
                        <>
                            {retraso.pendiente !== null && <Deshacer texto={retraso.pendiente.texto} clave={retraso.pendiente.clave} fondo="suave" movil={movil} onDeshacer={() => { retraso.deshacer(); }} />}
                            {soloLectura && (
                                <p className="rv-cn-nota-gris" role="note">
                                    <Icono nombre="eye" />
                                    <span>El negocio está en solo lectura: no se puede cambiar el canal. Desconectarlo sí.</span>
                                </p>
                            )}
                            <Campo etiqueta="Nombre" valor={ajustes.nombre} max={80} bloqueado={soloLectura} ayuda="Solo lo ve tu equipo, en el buzón y aquí." onChange={(v) => { setAjustes({ ...ajustes, nombre: v }); }} nombre="canal-nombre" />
                            {c.channel_type === 'email' && c.email_sender != null && (
                                <Campo etiqueta="Nombre del remitente" valor={ajustes.remitente} max={80} bloqueado={soloLectura} ayuda={`Lo que ven tus clientes en «De». Sale desde ${c.email_sender.from_address}.`} onChange={(v) => { setAjustes({ ...ajustes, remitente: v }); }} nombre="canal-remitente" />
                            )}
                            <Desplegable etiqueta="Local" valor={ajustes.sede} opciones={[{ valor: '', texto: 'Todos los locales' }, ...datos.sedes.map((s) => ({ valor: s.id, texto: s.nombre }))]} bloqueado={soloLectura} ayuda="Los mensajes de ese local salen por aquí." onChange={(v) => { setAjustes({ ...ajustes, sede: v }); }} nombre="canal-local" />
                            <Interruptor
                                etiqueta={`Por defecto para ${tipo.nombre}`}
                                ayuda={`Por aquí salen los mensajes de ${tipo.nombre}${ajustes.sede === '' ? '' : ` de ${sede(ajustes.sede)}`}.`}
                                icono="star"
                                valor={ajustes.porDefecto}
                                desactivado={soloLectura}
                                onChange={(v) => { setAjustes({ ...ajustes, porDefecto: v }); }}
                                linea="arriba"
                            />
                            <div className="rv-cn-desconectar">
                                <strong>Desconectar el canal</strong>
                                <span>Antes ves qué deja de pasar. Las conversaciones y su historial se conservan.</span>
                                <Boton tipo="secundario" rojo icono="unplug" movil={movil} onClick={() => { setMotivo(''); setIntentado(false); setDesconectar(true); }}>
                                    Desconectar
                                </Boton>
                            </div>
                        </>
                    )}
                </div>
            </PanelLateral>
            <Ventana
                abierta={desconectar}
                titulo={`¿Desconectar «${nombreDe(c)}»?`}
                texto="Esto es lo que pasa:"
                accion="Desconectar"
                accionIcono="unplug"
                ocupado={ocupado}
                ocupadoTexto="Desconectando…"
                movil={movil}
                onAccion={hacerDesconexion}
                onCerrar={() => { if (!ocupado) { setDesconectar(false); } }}
            >
                <div className="rv-cn-impacto">
                    {impactoDesconectar(c, datos.canales.some((x) => x.id !== c.id && vivo(x) && x.channel_type === c.channel_type)).map((f) => (
                        <p key={f.texto}>
                            <Icono nombre={f.icono} />
                            <span>{f.texto}</span>
                        </p>
                    ))}
                    <Campo etiqueta="Motivo" valor={motivo} max={500} filas={2} error={intentado && motivo.trim().length < MIN_MOTIVO ? 'Escribe el motivo: queda en Actividad.' : null} ayuda="Queda en Actividad." onChange={setMotivo} nombre="desconectar-motivo" />
                </div>
            </Ventana>
        </>
    );
}
